import React, { useState, useRef, useEffect } from "react";
import { Box, Typography } from "@material-ui/core";
import {
  HotelCarousel,
  HotelAddress,
  HotelAmenities,
  HotelReviewBadge,
  HotelShopChooseRoomSkeleton,
  HotelTripAdvisorReviews,
  HotelDescription,
  VoidWindowNotice,
  getCancellationPolicyInfo,
  getCancellationPolicyInfoV2,
  CheckInInstructions,
  SanitizedHtml,
} from "halifax";
import clsx from "clsx";
import {
  CancellationPolicyEnum,
  CancellationReason,
  HotelCancellationPolicyV2Enum,
  VIEWED_PACKAGE_HOTEL_SHOP_MAP,
} from "redmond";
import { IDesktopShopProps } from "./container";
import { DesktopHotelPhotoModal } from "../DesktopHotelPhotoModal";
import * as textConstants from "./textConstants";
import { HotelShopSmallMap } from "../HotelShopSmallMap/component";
import { DesktopHotelShopChooseRoom } from "../DesktopHotelShopChooseRoom";
import "./styles.scss";
import { DesktopRoomPicker } from "../DesktopRoomPicker";
import { HotelShopExpandedMap } from "../HotelShopExpandedMap";
import { DesktopRewardsHeader } from "../DesktopRewardsHeader";
import { PackagesShopProgressBar } from "../../../book/components";
import { ShopErrorModal } from "../ShopErrorModal";
import { goToAvailability } from "../../utils/queryStringHelpers";
import { PackageHotelShopCallState } from "../../reducer/state";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import dayjs from "dayjs";
import {
  AVAILABLE,
  getExperimentVariant,
  HOTELS_CANCELLATION_POLICY_EXPERIMENT,
  useExperiments,
} from "../../../../context/experiments";

export const DesktopShop = (props: IDesktopShopProps) => {
  const {
    hotelShopCallInProgress,
    hotelShopCallState,
    lodging,
    stayDates,
    fromDate,
    untilDate,
    roomInfoProductsType,
    handleReadyToRedirect,
    setRoomInfoProduct,
    setRoomProductIndex,
    hasFailed,
    history,
    origin,
    adultsCount,
    children,
    infants,
    stopsOption,
    fareClassOptionFilter,
    searchDistanceFromShopProperties,
    packagesByLodgingId,
    cancellationSummary,
    cheapestProduct,
  } = props;

  const [showExpandedHotelMap, setShowExpandedHotelMap] = useState(false);

  const expState = useExperiments();
  const cancellationPolicyExperimentEnabled =
    getExperimentVariant(
      expState.experiments,
      HOTELS_CANCELLATION_POLICY_EXPERIMENT
    ) === AVAILABLE;
  const cancellationPolicy = cheapestProduct?.cancellationPolicy
    ? getCancellationPolicyInfo(
        cheapestProduct?.cancellationPolicy,
        textConstants.getCfarSecondaryText
      )
    : null;
  const cancellationPolicyV2 = cheapestProduct?.cancellationPolicyV2
    ? getCancellationPolicyInfoV2(cheapestProduct?.cancellationPolicyV2)
    : null;
  const cancellationPolicyInfo = cancellationPolicyExperimentEnabled
    ? cancellationPolicyV2
    : cancellationPolicy;
  const satisfiesCaliforniaBill644 = Boolean(
    cancellationPolicyExperimentEnabled
      ? cheapestProduct?.cancellationPolicyV2?.CancellationPolicyV2 ===
          HotelCancellationPolicyV2Enum.NonRefundable &&
          cancellationSummary?.reasons.includes(
            CancellationReason.CaliforniaBill644
          )
      : cheapestProduct?.cancellationPolicy.CancellationPolicy ===
          CancellationPolicyEnum.NonRefundable &&
          cancellationSummary?.reasons.includes(
            CancellationReason.CaliforniaBill644
          )
  );

  const ref = useRef<HTMLDivElement>(null);

  const renderSkeleton = () => (
    <Box className="desktop-hotel-shop-skeleton">
      {!lodging && <HotelCarousel isSkeleton />}
      <Box className="content-container">
        {!lodging && (
          <>
            <Box className="section-wrapper-container">
              <Box className={clsx("section-wrapper")}>
                <HotelAddress isSkeleton />
                <HotelAmenities isSkeleton />
                <HotelReviewBadge isSkeleton />
              </Box>
              <Box className={clsx("section-wrapper")}>
                <HotelReviewBadge isSkeleton />
                <HotelReviewBadge isSkeleton />
                <HotelAmenities isSkeleton />
              </Box>
              <Box className={clsx("section-wrapper")}>
                <HotelAmenities isSkeleton />
                <HotelAddress isSkeleton />
                <HotelAmenities isSkeleton />
              </Box>
            </Box>
            <HotelShopChooseRoomSkeleton />
          </>
        )}

        {!!lodging && (
          <Box className="pkg-hotel-shop-room-skeletons">
            <HotelShopChooseRoomSkeleton />
            <HotelShopChooseRoomSkeleton />
            <HotelShopChooseRoomSkeleton />
          </Box>
        )}
      </Box>
    </Box>
  );

  const renderAvailability = () => {
    const { lodging: lodgingData } = lodging || {};

    if (!lodging) {
      return null;
    }

    return (
      <Box className={clsx("desktop-hotel-shop-container")}>
        <DesktopHotelPhotoModal />
        <Box className={clsx("hotel-shop-right-column")}>
          <DesktopHotelShopChooseRoom />
          {cancellationSummary?.reasons.includes(
            CancellationReason.CaliforniaBill644
          ) && <VoidWindowNotice />}
        </Box>
        {lodgingData?.amenities && lodgingData?.amenities.length > 0 && (
          <Box className={clsx("section-wrapper")}>
            <Typography variant="h2" className="hotel-amenities-title">
              Hotel Amenities
            </Typography>
            <HotelAmenities
              amenities={lodgingData?.amenities}
              displayCount={8}
              showAmenitiesText={`More Amenities (${
                (lodgingData?.amenities.length ?? 8) - 8
              })`}
              hideAmenityIconsInTooltip
            />
          </Box>
        )}
        <Box className={clsx("section-wrapper")}>
          <Typography variant="h2">About the Property</Typography>
          <HotelDescription
            description={lodging?.lodging?.description}
            onMapClick={() => {
              setShowExpandedHotelMap(true);

              const packagePricingDetails =
                packagesByLodgingId?.[lodging.lodging.id]?.packageDetails
                  .pricing;

              const packageDiscount =
                packagePricingDetails?.totalPackageSavings.fiat.value ?? 0;

              const packageDiscountPercentage =
                packageDiscount && packagePricingDetails
                  ? (packageDiscount /
                      packagePricingDetails?.strikethroughTotalPrice.fiat
                        .value) *
                    100
                  : 0;

              trackEvent({
                eventName: VIEWED_PACKAGE_HOTEL_SHOP_MAP,
                properties: {
                  ...searchDistanceFromShopProperties?.properties,
                  package_discount: packageDiscount,
                  package_discount_percentage: packageDiscountPercentage,
                },
                encryptedProperties: [
                  ...searchDistanceFromShopProperties?.encryptedProperties,
                ],
              });
            }}
            map={
              <HotelShopSmallMap
                lodging={lodging}
                setShowExpandedHotelMap={setShowExpandedHotelMap}
                searchDistanceFromShopProperties={
                  searchDistanceFromShopProperties
                }
                lodgingPackage={packagesByLodgingId?.[lodging.lodging.id]}
              />
            }
          />
          <HotelShopExpandedMap
            lodging={lodging}
            showExpandedHotelMap={showExpandedHotelMap}
            setShowExpandedHotelMap={setShowExpandedHotelMap}
          />
        </Box>
        {lodgingData?.tripAdvisorReviews &&
          lodgingData?.tripAdvisorReviews?.comments?.length > 0 && (
            <Box className={clsx("section-wrapper")}>
              <Typography variant="h2">Customer Reviews</Typography>
              <HotelTripAdvisorReviews lodgingData={lodgingData} trim={1000} />
            </Box>
          )}
        {renderCancellationPolicyInfo(
          cancellationPolicyInfo,
          satisfiesCaliforniaBill644
        )}
        {lodgingData?.checkInInstructions &&
          stayDates?.from &&
          stayDates?.until && (
            <Box className={clsx("section-wrapper")}>
              <Typography
                variant="h4"
                className="hotel-shop-check-in-schedule-heading"
              >
                {textConstants.CHECK_IN_INSTRUCTIONS_TITLE}
              </Typography>
              <SanitizedHtml
                as={Typography}
                variant="body2"
                html={textConstants.formattedCheckIn(
                  dayjs(stayDates.from).toDate(),
                  lodgingData.checkInInstructions?.checkInTime
                )}
              />
              <SanitizedHtml
                as={Typography}
                variant="body2"
                html={textConstants.formattedCheckOut(
                  dayjs(stayDates.until).toDate(),
                  lodgingData.checkInInstructions?.checkOutTime
                )}
              />
            </Box>
          )}
        {lodgingData?.checkInInstructions &&
        lodgingData?.checkInInstructions?.policies?.length ? (
          <Box className={clsx("section-wrapper")}>
            <CheckInInstructions
              policies={lodgingData?.checkInInstructions?.policies}
            />
          </Box>
        ) : undefined}
      </Box>
    );
  };

  const renderCancellationPolicyInfo = (
    cancellationPolicyInfo: {
      className: string;
      primaryText: string;
      secondaryText?: string;
      tagText: String;
    } | null,
    satisfiesCaliforniaBill644: boolean
  ) => {
    if (cancellationPolicyExperimentEnabled) {
      return (
        satisfiesCaliforniaBill644 &&
        cancellationPolicyInfo && (
          <Box className={clsx("section-wrapper")}>
            <Typography
              variant="h4"
              className="hotel-shop-cancellation-policy-heading"
            >
              {cancellationPolicyInfo.primaryText}
            </Typography>
            <Typography variant="subtitle2">
              {textConstants.CALIFORNIA_BILL_644_CANCELLATION_TEXT}
            </Typography>
          </Box>
        )
      );
    } else {
      return (
        cancellationPolicyInfo && (
          <Box className={clsx("section-wrapper")}>
            <Typography
              variant="h4"
              className="hotel-shop-cancellation-policy-heading"
            >
              {cancellationPolicyInfo.primaryText}
            </Typography>
            <Typography variant="subtitle2">
              {satisfiesCaliforniaBill644
                ? textConstants.CALIFORNIA_BILL_644_CANCELLATION_TEXT
                : cancellationPolicyInfo.secondaryText}
            </Typography>
          </Box>
        )
      );
    }
  };

  const renderShop = () => {
    if (!lodging) {
      return null;
    }
    return (
      <div ref={ref}>
        <Box
          id="hotel-shop-room-type-section"
          className={clsx(
            "hotel-shop-room-type-section-wrapper-redesign",
            "white-bg"
          )}
        >
          <Typography className="choose-bed-type-header" variant="h2">
            Choose Room
          </Typography>
          <DesktopRoomPicker
            handleReadyToRedirect={handleReadyToRedirect}
            roomInfoProductsType={roomInfoProductsType}
            setRoomInfoProduct={setRoomInfoProduct}
            setRoomProductIndex={setRoomProductIndex}
            nonRefundablePolicyOverrideText={
              cancellationSummary?.reasons.includes(
                CancellationReason.CaliforniaBill644
              )
                ? textConstants.CALIFORNIA_BILL_644_CANCELLATION_TEXT
                : undefined
            }
          />
        </Box>
      </div>
    );
  };

  const [successShopSearchFromDate, setSuccessShopSearchFromDate] =
    useState<Date | null>(null);
  const [successShopSearchUntilDate, setSuccessShopSearchUntilDate] =
    useState<Date | null>(null);

  useEffect(() => {
    if (hotelShopCallState === PackageHotelShopCallState.Success) {
      setSuccessShopSearchFromDate(fromDate);
      setSuccessShopSearchUntilDate(untilDate);
    }
  }, [hotelShopCallState]);

  const returnToAvailability = (
    prevSuccessHotelFromDate?: Date | null,
    prevSuccessHotelUntilDate?: Date | null
  ) => {
    goToAvailability({
      history,
      origin,
      lodging,
      fromDate,
      untilDate,
      adultsCount,
      children,
      infants,
      stopsOption,
      prevSuccessHotelFromDate,
      prevSuccessHotelUntilDate,
      fareClassOptionFilter,
    });
  };

  return hasFailed ? (
    <ShopErrorModal
      returnToAvailability={() =>
        returnToAvailability(
          successShopSearchFromDate,
          successShopSearchUntilDate
        )
      }
    />
  ) : (
    <Box className="desktop-package-hotel-shop-wrapper">
      <DesktopRewardsHeader />
      <PackagesShopProgressBar />
      {renderAvailability()}
      {hotelShopCallInProgress ? renderSkeleton() : renderShop()}
    </Box>
  );
};
